import { useCallback, useLayoutEffect, useMemo } from 'react';

import { useForceUpdate, useMeasure } from '@spazious/react-hooks';

import { DataTableLayoutManager } from '../DataTableLayoutManager.js';
import { DataTableColumns, DataTableRow } from '../DataTableTypes.js';

export declare type MeasureRef<E extends Element = Element> = (element: E) => void;

export type DataTableHookOptions<TRow extends DataTableRow, TContext = unknown> = {
  columns: DataTableColumns<TRow, TContext>;
  rows: TRow[];
  context?: TContext;
};

export type DataTableHookResult<TRow extends DataTableRow, TContext = unknown> = {
  columns: DataTableColumns<TRow, TContext>;
  rows: TRow[];
  register: (columnIdx: number, rowIdx: number) => (ref: HTMLElement) => void;
  unregister: (columnIdx: number, rowIdx: number) => void;
  update: () => void;
  containerRef: MeasureRef<HTMLElement>;
  manager: DataTableLayoutManager<TRow, TContext>;
  context?: TContext;
};

export const useDataTable = <TRow extends DataTableRow, TContext = unknown>(
  opts: DataTableHookOptions<TRow, TContext>
): DataTableHookResult<TRow, TContext> => {
  const [containerRef, { width: containerWidth }] = useMeasure();
  const forceUpdate = useForceUpdate();
  const { columns, rows } = opts;

  const layoutManager = useMemo(() => new DataTableLayoutManager<TRow, TContext>(), []);

  const { unregister: unregisterCell, register: registerCell } = layoutManager;

  /**
   * Register a cell ref for a certain column and row
   */
  const register = useCallback(
    (columnIdx: number, rowIdx: number) => (ref: HTMLElement) => {
      registerCell(columnIdx, rowIdx, ref);
    },
    [registerCell]
  );

  /**
   * Register a cell ref for a certain column and row
   */
  const unregister = useCallback(
    (columnIdx: number, rowIdx: number) => {
      unregisterCell(columnIdx, rowIdx);
    },
    [unregisterCell]
  );

  const update = useCallback(() => {
    forceUpdate();
    layoutManager.update();
  }, [forceUpdate, layoutManager]);

  useLayoutEffect(() => {
    layoutManager.updateContainerWidth(containerWidth);
    layoutManager.updateColumns(columns);
    layoutManager.update();
  }, [columns, containerWidth, layoutManager, update]);

  return useMemo(
    () => ({
      columns,
      rows,
      register,
      unregister,
      update,
      containerRef,
      manager: layoutManager,
      context: opts.context,
    }),
    [columns, rows, register, unregister, update, containerRef, layoutManager, opts.context]
  );
};
